import { useState } from 'react';

// ============================================================
// 2026-07-16 リードDB統一:
// 旧送信先 readdy.ai/api/form/… はReaddy解約(2026-04-10)により404＝
// フォームがエラー表示になりCV全件ロスト状態だった。
// gas-questionnaire経由でAIシート「LP完結リード」へ集約（lp02等と同じ経路）
// ============================================================
const GAS_ENDPOINT = 'https://script.google.com/macros/s/AKfycbwMIUr0pXSiubpQ4w7mYoO8WE9RTanN2b1ZAcwPiqky0dh4K7oPvivWlo8asOBPuJPi/exec';

const generateUUID = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });

// 全LP共通のvisitor_id（localStorageキーも共通＝LP横断で同一ユーザーを識別）
const getOrCreateVisitorId = (): string => {
  const KEY = 'lp_common_visitor_id';
  const existing = localStorage.getItem(KEY);
  if (existing) return existing;
  const id = `vis_${generateUUID()}`;
  localStorage.setItem(KEY, id);
  return id;
};

const generateConversionId = (): string => {
  const now = new Date();
  const d = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let r = '';
  for (let i = 0; i < 8; i++) r += chars.charAt(Math.floor(Math.random() * chars.length));
  return `cvid_${d}_${r}`;
};

interface FormPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FormPopup({ isOpen, onClose }: FormPopupProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    postalCode: '',
    birthDate: '',
    transferTiming: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // birthDate (YYYY-MM-DD) → 年/月/日に分解
      const [by = '', bm = '', bd = ''] = (formData.birthDate || '').split('-');
      const params = new URLSearchParams(window.location.search);

      const payload = {
        visitor_id: getOrCreateVisitorId(),
        conversion_id: generateConversionId(),
        lp_id: window.location.origin + window.location.pathname,
        qualification: '',
        birth_year: by,
        birth_month: bm ? String(Number(bm)) : '',
        birth_day: bd ? String(Number(bd)) : '',
        name: formData.name,
        phone: formData.phone.replace(/-/g, ''),
        email: formData.email,
        postal_code: formData.postalCode,
        prefecture: '',
        work_style: '',
        transfer_timing: formData.transferTiming,
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_term: params.get('utm_term') || '',
        utm_content: params.get('utm_content') || '',
        aff_company: params.get('aff_company') || '',
        aff_id: params.get('aff_id') || '',
        click_id: params.get('click_id') || '',
        referrer: document.referrer || '',
        landing_page: window.location.href,
        submitted_at: new Date().toISOString(),
        user_agent: navigator.userAgent,
      };

      // no-cors のためレスポンスは読めない（fetch解決＝送信成功として扱う）
      await fetch(GAS_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // GTMへカスタムイベントを送信
      if (window && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'form_submission_success',
        });
      }

      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        postalCode: '',
        birthDate: '',
        transferTiming: ''
      });
      setTimeout(() => {
        onClose();
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto touch-none">
      <div className="bg-white rounded-lg w-full max-w-md my-8 max-h-[90vh] overflow-y-auto flex-shrink-0 touch-pan-y">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between rounded-t-lg z-10">
          <h3 className="text-lg font-bold text-gray-900">連絡先入力フォーム</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-5">
          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-5">
              <p className="text-sm text-green-800 text-center font-bold">
                送信が完了しました
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5">
              <p className="text-sm text-red-800 text-center font-bold">
                送信に失敗しました。もう一度お試しください。
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                お名前 <span className="text-pink-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="山田 花子"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                電話番号 <span className="text-pink-500">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="09012345678"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                メールアドレス <span className="text-pink-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="example@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                郵便番号 <span className="text-pink-500">*</span>
              </label>
              <input
                type="text"
                name="postalCode"
                required
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                生年月日 <span className="text-pink-500">*</span>
              </label>
              <input
                type="date"
                name="birthDate"
                required
                value={formData.birthDate}
                onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                転職希望時期 <span className="text-pink-500">*</span>
              </label>
              <select
                name="transferTiming"
                required
                value={formData.transferTiming}
                onChange={(e) => setFormData({ ...formData, transferTiming: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="すぐにでも">すぐにでも</option>
                <option value="1ヶ月以内">1ヶ月以内</option>
                <option value="3ヶ月以内">3ヶ月以内</option>
                <option value="6ヶ月以内">6ヶ月以内</option>
                <option value="良い求人があれば">良い求人があれば</option>
                <option value="まずは情報収集">まずは情報収集</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? '送信中...' : '求人条件のヒアリングへ'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}